import { FormattedMessage } from "react-intl";

import Divider from "components/v2/Divider/Divider";
import { formatDate } from "helpers/dateTimeHelper";
import { extractAllFilesFromMessages } from "helpers/trackingHelpers";
import { hasLink } from "helpers/urlHelpers";
import ActivityItem from "../ActivityItem/ActivityItem";
import { useGetDefaultFilesReturn } from "../ActivityFiles/useGetDefaultFiles";

const isSystemUpdate = (update: any) =>
  update.author_name === "System" &&
  !["Cargobase", "Cargobase System"].includes(update?.company?.company_name) &&
  update.type !== "delivered" &&
  !hasLink(update.message);

// Message types to remove.
// Other types that exist (that we want to keep are: ["general", "upload", "delivered", "flight"];
const isTypeToHide = (type: string) => ["webhook", "schedule_change", "service_change", "auto_update"].includes(type);

interface ActivityListProps {
  /**
   * The tracking updates data.
   */
  trackingUpdatesData: any;
  /**
   * The shipment details data from getShipmentData.
   */
  shipmentDetails: any;
  /**
   * Whether to only display comments.
   */
  viewCommentsOnly?: boolean;
  /**
   * The default files data.
   */
  defaultFilesData?: useGetDefaultFilesReturn;
  /**
   * The links extracted from messages.
   */
  filesExtractedFromMessages?: ReturnType<typeof extractAllFilesFromMessages>;
}

export default function ActivityList({
  trackingUpdatesData,
  shipmentDetails,
  viewCommentsOnly,
  defaultFilesData,
  filesExtractedFromMessages,
}: ActivityListProps) {
  const { booking_date } = shipmentDetails?.schedule ?? {};
  const updates = trackingUpdatesData?.updates || [];
  const nonSystemUpdates = updates.filter((update: any) => !isSystemUpdate(update));
  const nonUsedTypesUpdates = nonSystemUpdates.filter((update: any) => !isTypeToHide(update.type));
  // filter any system updates that have type upload but have no attachments.
  const finalUpdatesToShow = nonUsedTypesUpdates.filter(
    (update: any) =>
      !(
        ["Cargobase", "Cargobase System"].includes(update?.company?.company_name) &&
        update.type === "upload" &&
        (!update?.data || !update?.data?.length)
      ),
  );

  const filteredUpdates = viewCommentsOnly
    ? finalUpdatesToShow.filter((update: any) => Boolean(update.message) && update.is_user_comment)
    : finalUpdatesToShow;

  const filteredUpdatesWithDefaultFiles = defaultFilesData?.defaultFilesMessage
    ? [defaultFilesData.defaultFilesMessage, ...filteredUpdates]
    : filteredUpdates;

  return (
    <>
      <div>
        {[...filteredUpdatesWithDefaultFiles].reverse().map((update: any, index: number) => (
          <ActivityItem key={index} {...update} filesExtractedFromMessages={filesExtractedFromMessages} />
        ))}
      </div>
      {booking_date && (
        <Divider
          label={
            <div className="text-center">
              <p className="m-0 text-xs text-tertiary font-medium">
                <FormattedMessage id="tracking.activity.shipmentBooked" defaultMessage="Shipment booked" />
              </p>
              <p className="m-0 text-xs text-tertiary font-regular">{formatDate(booking_date, "DD MMM YYYY, HH:mm")}</p>
            </div>
          }
        />
      )}
    </>
  );
}

ActivityList.defaultProps = {
  viewCommentsOnly: false,
  filesExtractedFromMessages: undefined,
  defaultFilesData: undefined,
};
